import { supabase } from "../supabaseClient";
import { uploadImageToR2 } from "./r2ImageService";

/**
 * Add or edit a user card. New image File objects are uploaded first, then the
 * existing add_card / edit_card RPC remains the authority for persistence.
 */
export async function saveCard(cardData, userId, isEdit = false, cardId = null) {
  if (!userId) throw new Error("saveCard requires a user id");

  try {
    const imageFiles = Array.isArray(cardData.images) ? cardData.images : [];
    const uploadedImages = await Promise.all(
      imageFiles.map((file) => uploadImageToR2(file))
    );

    const attributes = { ...(cardData.attributes || {}) };

    // New cards start at position 0. When editing an existing card, append
    // newly uploaded images after the highest persisted image position.
    // Previously every edit started new images at 0, which could create
    // duplicate positions such as [0, 0] for front/back images.
    let imagePositionOffset = 0;

    if (isEdit && cardId && uploadedImages.length > 0) {
      const { data: lastImage, error: positionError } = await supabase
        .from("item_images")
        .select("position")
        .eq("user_item_id", cardId)
        .order("position", { ascending: false, nullsFirst: false })
        .limit(1)
        .maybeSingle();

      if (positionError) {
        console.error("Failed to determine next image position:", positionError);
        throw positionError;
      }

      const highestPosition = Number(lastImage?.position);
      imagePositionOffset = Number.isFinite(highestPosition)
        ? highestPosition + 1
        : 0;
    }

    const commonPayload = {
      user_id: cardData.user_id || userId,
      name: cardData.name || cardData.suggested_name || "",
      brand: cardData.brand || "",
      set_name: cardData.set_name || "",
      subset: cardData.subset || attributes.subset || null,
      year: cardData.year || null,
      year_end: cardData.year_end || null,
      card_number: cardData.card_number || attributes.card_number || null,
      category_id: cardData.category_id || null,
      subcategory_id: cardData.subcategory_id || null,
      attributes,
      description: cardData.description || null,
      price_paid: cardData.price_paid || null,
      estimated_value: cardData.estimated_value || null,
      for_sale: cardData.for_sale ?? false,
      is_private: cardData.is_private ?? false,
      images: uploadedImages.map((image, index) => ({
        ...image,
        position: imagePositionOffset + index,
      })),
      variants: cardData.variants || cardData.variations || [],
    };

    const payload = isEdit
      ? {
          ...commonPayload,
          item_id: cardId,
        }
      : {
          ...cardData,
          ...commonPayload,
        };

    const { data, error } = await supabase.rpc(
      isEdit ? "edit_card" : "add_card",
      { payload }
    );

    if (error) {
      console.error("Supabase RPC error:", error);
      throw error;
    }

    if (!data) {
      throw new Error("No data returned from saveCard RPC");
    }

    return Array.isArray(data) ? data[0] : data;
  } catch (err) {
    console.error("Failed to save card:", err);
    throw err;
  }
}

export async function removeUserItemVariant(userItemId, variantId) {
  const { error } = await supabase
    .from("user_item_variants")
    .delete()
    .match({ user_item_id: userItemId, variant_id: variantId });

  if (error) {
    console.error("Failed to remove variant:", error);
    throw error;
  }
}

export async function deleteCardImage({ imageId }) {
  if (!imageId) {
    throw new Error("deleteCardImage requires an image id");
  }

  const { data, error } = await supabase.functions.invoke(
    "delete-r2-media",
    {
      body: {
        resourceType: "item_image",
        resourceId: String(imageId),
      },
    }
  );

  if (error) {
    console.error("R2 image deletion failed:", error);
    throw error;
  }

  if (!data?.success) {
    console.error("R2 image deletion failed:", data);
    throw new Error(data?.error || "Unable to delete R2 image");
  }

  return true;
}
